import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit'
import {
  ErrorResponse,
  ForgotUser,
  LoginPayload,
  resetPayload,
  User,
} from '@school/schemas/typeInterface'
import { AxiosError, AxiosResponse } from 'axios'
import {
  constructApiEndpoint,
  httpPost,
  httpPut,
} from '@school/common/common-utils/helpers/httpClient'
import { RootState } from '@school/store/store'
import {
  CHECKEMAILEXIST,
  LOGIN,
  RESETAUTHPASSWORD,
  UPDATEPASSWORD,
} from '@school/common/common-utils/constants/slice-constants'
import { toast } from 'react-toastify'

// Async thunk for check user exists
export const checkUserExists = createAsyncThunk(
  CHECKEMAILEXIST,
  async (payload: ForgotUser, { rejectWithValue }) => {
    try {
      const endpoint = constructApiEndpoint(CHECKEMAILEXIST, {})
      const response: AxiosResponse = await httpPost(endpoint, payload)
      return response?.data
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        const message =
          error.response?.data?.message?.[0] || error.message || 'Failed to fetch user'
        return rejectWithValue(message)
      }
      return rejectWithValue('An unexpected error occurred')
    }
  },
)
// Async thunk for login
export const loginUser = createAsyncThunk(
  LOGIN,
  async (payload: LoginPayload, { rejectWithValue }) => {
    try {
      const endpoint = constructApiEndpoint(LOGIN, {})
      const response: AxiosResponse<User> = await httpPost(endpoint, payload)
      return response?.data
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        const errorData = error.response?.data as ErrorResponse
        const message = errorData?.message?.[0] || error.message || 'Failed to fetch schools'
        return rejectWithValue(message)
      }
      return rejectWithValue('An unexpected error occurred')
    }
  },
)

// Async thunk for update password
export const updateAuthPassword = createAsyncThunk(
  RESETAUTHPASSWORD,
  async (payload: resetPayload, { rejectWithValue }) => {
    try {
      const endpoint = constructApiEndpoint(RESETAUTHPASSWORD, {})
      const response: AxiosResponse = await httpPut(endpoint, payload)
      toast.success(response.data?.message)
      return response
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        const message = error.response?.data?.message?.[0] || error.message || 'Failed to update'
        return rejectWithValue(message)
      }
      return rejectWithValue('An unexpected error occurred')
    }
  },
)

// Async thunk for update password
export const updatePassword = createAsyncThunk(
  UPDATEPASSWORD,
  async (payload: resetPayload, { rejectWithValue }) => {
    try {
      const endpoint = constructApiEndpoint(UPDATEPASSWORD, {})
      const response: AxiosResponse = await httpPut(endpoint, payload)
      toast.success(response.data?.message)
      return response
    } catch (error: unknown) {
      if (error instanceof AxiosError) {
        const message = error.response?.data?.message?.[0] || error.message || 'Failed to update'
        return rejectWithValue(message)
      }
      return rejectWithValue('An unexpected error occurred')
    }
  },
)

interface AuthState {
  user: User | null
  userToken: string | null
  loading: boolean
  error: string | null
}

const initialState: AuthState = {
  user: null,
  loading: false,
  userToken: null,
  error: null,
}

// Create slice
const authSlice = createSlice({
  name: 'auth',
  initialState,
  reducers: {
    logout: (state) => {
      state.user = null
      state.error = null
      state.loading = false
    },
    createSession: (state, action: PayloadAction<{ user: User; token: string }>) => {
      state.user = action.payload.user
      state.userToken = action.payload.token
    },
    updateSession: (state, action: PayloadAction<{ user: Partial<User>; token?: string }>) => {
      if (state.user) {
        state.user = { ...state.user, ...action.payload.user }
      }
      if (action.payload.token) {
        state.userToken = action.payload.token
      }
    },
    destroySession: (state) => {
      state.user = null
      state.userToken = null
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(loginUser.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(loginUser.fulfilled, (state, action) => {
        state.loading = false
        state.user = action?.payload?.data
      })
      .addCase(loginUser.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
      //update password
      .addCase(updatePassword.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updatePassword.fulfilled, (state, action) => {
        state.loading = false
        state.user = action?.payload?.data
      })
      .addCase(updatePassword.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
      //RESET LOGINED USER PASSWORD
      .addCase(updateAuthPassword.pending, (state) => {
        state.loading = true
        state.error = null
      })
      .addCase(updateAuthPassword.fulfilled, (state, action) => {
        state.loading = false
        state.user = action?.payload?.data
      })
      .addCase(updateAuthPassword.rejected, (state, action) => {
        state.loading = false
        state.error = action.payload as string
      })
  },
})

// Export actions and reducer
export const { createSession, updateSession, destroySession, logout } = authSlice.actions
// Selector to access auth state
export const selectAuthUser = (state: RootState) => state.auth.user
export const selectAuthToken = (state: RootState) => state.auth.userToken

export default authSlice.reducer
